import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const GestionTickets = () => {
  return (
    <>
      <Helmet>
        <title>Gestion des Tickets - ZiExter | Traitement et suivi de tickets support</title>
        <meta name="description" content="Externalisez la gestion de vos tickets support. Catégorisation, traitement, escalade et suivi jusqu'à résolution complète." />
        <link rel="canonical" href="https://ziexter.com/services/gestion-des-tickets" />
      </Helmet>

      <section className="page-hero bg-gradient-to-br from-secondary to-primary text-white">
        <div className="container-custom">
          <div className="max-w-3xl animate-fade-in-up">
            <nav className="flex items-center space-x-2 text-sm mb-4 text-gray-200">
              <Link to="/" className="hover:text-white">Accueil</Link>
              <span>/</span>
              <Link to="/services" className="hover:text-white">Services</Link>
              <span>/</span>
              <span className="text-white">Gestion des Tickets</span>
            </nav>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Gestion des Tickets – ZiExter
            </h1>
            <p className="text-xl text-gray-100 mb-4">
              Externalisez le traitement et le suivi de vos tickets support
            </p>
            <p className="text-lg text-gray-200">
              De la réception à la résolution complète, ZiExter prend en charge vos tickets clients : catégorisation, traitement, escalade et reporting détaillé.
            </p>
          </div>
        </div>
      </section>

      <section className="py-8 bg-accent">
        <div className="container-custom">
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/devis" className="btn btn-secondary">Découvrez nos solutions</Link>
            <Link to="/contact" className="btn btn-outline-white">Échangez avec un expert</Link>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto animate-fade-in-up">
            <h2 className="section-title text-center mb-8">Des tickets qui s'accumulent… et des clients qui s'impatientent</h2>
            <p className="text-lg text-gray-700 mb-4">
              Votre boîte mail déborde, vos tickets support s'empilent, et vos clients attendent une réponse depuis des jours. Résultat : satisfaction en chute libre et équipes débordées.
            </p>
            <p className="text-lg font-semibold text-primary">
              Avec ZiExter, confiez la gestion complète de vos tickets à des experts formés. Nous traitons, catégorisons, escaladons et suivons chaque demande jusqu'à sa résolution.
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Notre service de gestion des tickets</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: 'Réception et tri des tickets', desc: 'Centralisation de toutes vos demandes (email, formulaire, chat, téléphone) dans votre outil (Zendesk, Freshdesk, Jira, etc.).' },
              { title: 'Catégorisation et priorisation', desc: 'Classification automatique par type, urgence et département concerné.' },
              { title: 'Traitement de premier niveau', desc: 'Résolution immédiate des demandes simples et récurrentes (FAQ, réinitialisation, guides).' },
              { title: 'Escalade intelligente', desc: 'Transfert des tickets complexes vers vos experts internes avec contexte complet.' },
              { title: 'Suivi jusqu\'à résolution', desc: 'Relances régulières, mise à jour du statut, validation de la satisfaction client.' },
              { title: 'Reporting et analyse', desc: 'Tableaux de bord en temps réel : volume de tickets, temps de traitement, satisfaction, tendances.' }
            ].map((item, i) => (
              <div key={i} className="card hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                <h3 className="font-bold mb-2">{item.title}</h3>
                <p className="text-sm text-gray-700">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Ils nous confient leurs tickets</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'SaaS B2B, France', problem: 'Délai de réponse > 48h, clients mécontents.', solution: 'Gestion complète des tickets niveau 1 et 2.', result: 'Temps de réponse réduit à 2h, satisfaction +35%.' },
              { title: 'E-commerce, Belgique', problem: 'Pics saisonniers impossibles à gérer.', solution: 'Équipe dédiée extensible selon les volumes.', result: 'Aucun ticket en retard même en période de soldes.' },
              { title: 'Éditeur logiciel, Suisse', problem: 'Équipe support surchargée, turnover élevé.', solution: 'Externalisation complète de la gestion des tickets.', result: 'Équipe interne concentrée sur R&D, coûts -40%.' }
            ].map((item, i) => (
              <div key={i} className="card hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 100}ms` }}>
                <h3 className="font-bold mb-3">{item.title}</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li><strong>Problème :</strong> {item.problem}</li>
                  <li><strong>Solution ZiExter :</strong> {item.solution}</li>
                  <li><strong>Résultat :</strong> {item.result}</li>
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Performance en chiffres</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: '🎫', stat: '15 000', label: 'tickets traités chaque mois' },
              { icon: '⏱️', stat: '< 2h', label: 'Temps moyen de première réponse' },
              { icon: '✅', stat: '85%', label: 'Tickets résolus dès le premier contact' },
              { icon: '⭐', stat: '96%', label: 'Taux de satisfaction client' }
            ].map((item, i) => (
              <div key={i} className="card text-center hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="text-3xl mb-2">{item.icon}</div>
                <div className="text-2xl font-bold text-primary mb-1">{item.stat}</div>
                <p className="text-sm text-gray-600">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Avis Clients</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { quote: 'ZiExter a transformé notre support client. Nos tickets sont traités en un temps record.', name: 'Marc Dupuis', role: 'CTO, SaaS B2B' },
              { quote: 'Nous pouvons enfin gérer les pics de demandes sans recruter en urgence. Un vrai soulagement.', name: 'Sophie Lefèvre', role: 'Responsable service client, E-commerce' },
              { quote: 'Professionnalisme, réactivité, reporting impeccable. Je recommande sans hésiter.', name: 'Thomas Berger', role: 'CEO, Éditeur logiciel' }
            ].map((item, i) => (
              <div key={i} className="card hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 100}ms` }}>
                <p className="text-gray-700 mb-4 italic">"{item.quote}"</p>
                <div className="font-semibold">{item.name}</div>
                <div className="text-sm text-gray-600">{item.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">FAQ</h2>
          </div>
          <div className="max-w-4xl mx-auto space-y-4">
            {[
              { q: 'Quels outils de ticketing utilisez-vous ?', a: 'Zendesk, Freshdesk, Jira Service Desk, Salesforce Service Cloud, Intercom, et bien d\'autres.' },
              { q: 'Pouvez-vous gérer des tickets en plusieurs langues ?', a: 'Oui, nous traitons des tickets en français, anglais, espagnol, arabe, etc.' },
              { q: 'Comment sont formés vos agents ?', a: 'Formation initiale sur votre produit/service + formation continue. Accès à votre base de connaissances.' },
              { q: 'Que se passe-t-il si un ticket nécessite une expertise pointue ?', a: 'Nous l\'escaladons immédiatement vers vos experts avec tout le contexte nécessaire.' },
              { q: 'Puis-je commencer par un petit volume de tickets ?', a: 'Absolument. Nous adaptons nos ressources à vos besoins et évoluons avec vous.' }
            ].map((item, i) => (
              <details key={i} className="card cursor-pointer">
                <summary className="font-semibold p-4 flex justify-between items-center">
                  <span>Q : {item.q}</span>
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="p-4 pt-0 text-gray-600">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gradient-to-r from-primary to-secondary text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center animate-fade-in-up">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Zéro ticket en retard, clients satisfaits
            </h2>
            <p className="text-xl text-gray-100 mb-8">
              Avec ZiExter, chaque demande client est traitée rapidement et professionnellement. Concentrez-vous sur votre produit, nous gérons le support.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn btn-accent btn-lg">Contactez notre équipe support</Link>
              <Link to="/devis" className="btn btn-outline-white btn-lg">Demandez un devis personnalisé</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default GestionTickets;
